import type { QueryClient, QueryKey } from '@tanstack/react-query'
import { pageVisible } from '../state/use-page-visible'

export const TENANT_COLLECTION_COMMITTED_EVENT = 'YIHANG_EXTENSION_COLLECTION_COMMITTED'

const collectionRefreshKeys: readonly QueryKey[] = [
  ['products'],
  ['product-page'],
  ['wb-library-page'],
  ['offers'],
  ['listing-groups'],
  ['dashboard'],
  ['jobs'],
]

export interface ExtensionCollectionCommitted {
  type: typeof TENANT_COLLECTION_COMMITTED_EVENT
  requestId: string
  productId: string
  jobId: string
  committedAt: string
  tenantId?: string
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu

interface CollectionEventTarget {
  readonly location: { readonly origin: string }
  addEventListener(type: 'message', listener: EventListener): void
  removeEventListener(type: 'message', listener: EventListener): void
}

export type ExtensionCollectionCommittedHandler = (event: ExtensionCollectionCommitted) => void

export function parseExtensionCollectionCommitted(
  value: unknown,
): ExtensionCollectionCommitted | null {
  if (!value || typeof value !== 'object') return null
  const candidate = value as Partial<ExtensionCollectionCommitted>
  if (
    candidate.type !== TENANT_COLLECTION_COMMITTED_EVENT ||
    typeof candidate.requestId !== 'string' ||
    typeof candidate.productId !== 'string' ||
    typeof candidate.jobId !== 'string' ||
    typeof candidate.committedAt !== 'string' ||
    !Number.isFinite(Date.parse(candidate.committedAt))
  )
    return null
  const tenantId = typeof candidate.tenantId === 'string' ? candidate.tenantId.trim() : ''
  return {
    ...(candidate as ExtensionCollectionCommitted),
    ...(UUID_PATTERN.test(tenantId) ? { tenantId } : {}),
  }
}

export function attachExtensionCollectionEvents(
  queryClient: Pick<QueryClient, 'invalidateQueries'>,
  target: CollectionEventTarget = window,
  onCommitted?: ExtensionCollectionCommittedHandler,
): () => void {
  let timer: ReturnType<typeof setTimeout> | undefined
  let pending = false
  const refresh = (keys: readonly QueryKey[]) =>
    Promise.all(
      keys.map((queryKey) => queryClient.invalidateQueries({ queryKey, refetchType: pageVisible() ? 'active' : 'none' })),
    ).catch(() => undefined)
  const listener = (event: MessageEvent<unknown>) => {
    if (event.source !== (target as unknown) || event.origin !== target.location.origin) return
    const committed = parseExtensionCollectionCommitted(event.data)
    if (!committed) return
    onCommitted?.(committed)
    pending = true
    if (timer) return
    const flush = () => {
      if (!pending) { timer = undefined; return }
      pending = false
      void refresh(collectionRefreshKeys)
      timer = setTimeout(flush, 15_000)
    }
    flush()
  }
  target.addEventListener('message', listener as EventListener)
  return () => {
    target.removeEventListener('message', listener as EventListener)
    clearTimeout(timer)
  }
}
